import * as THREE from "three";
import type { ArenaLayout } from "@neon-arena/shared";

export class ArenaRenderer {
  group = new THREE.Group();
  private gridMaterial!: THREE.ShaderMaterial;
  private layout: ArenaLayout;

  constructor(layout: ArenaLayout) {
    this.layout = layout;
    this.buildFloor();
    this.buildWalls();
    this.buildObstacles();
    this.buildSky();
  }

  update(t: number): void {
    if (this.gridMaterial) {
      this.gridMaterial.uniforms.uTime.value = t;
    }
  }

  private buildFloor(): void {
    const size = this.layout.size;
    const geo = new THREE.PlaneGeometry(size, size, 1, 1);
    geo.rotateX(-Math.PI / 2);

    this.gridMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(0x22d3ee) },
        uColorAlt: { value: new THREE.Color(0xa855f7) },
      },
      transparent: true,
      depthWrite: false,
      vertexShader: /* glsl */ `
        varying vec3 vWorld;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorld = wp.xyz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        uniform vec3 uColor;
        uniform vec3 uColorAlt;
        varying vec3 vWorld;
        float gridLine(float coord, float spacing, float lineWidth) {
          float d = abs(fract(coord / spacing - 0.5) - 0.5) * spacing;
          return 1.0 - smoothstep(0.0, lineWidth, d);
        }
        void main() {
          float g1 = gridLine(vWorld.x, 4.0, 0.05);
          float g2 = gridLine(vWorld.z, 4.0, 0.05);
          float g3 = gridLine(vWorld.x, 16.0, 0.10);
          float g4 = gridLine(vWorld.z, 16.0, 0.10);
          float fine = max(g1, g2);
          float bold = max(g3, g4);
          vec3 col = mix(uColor * 0.55, uColorAlt, bold);
          float pulse = 0.7 + 0.3 * sin(uTime * 1.4 + (vWorld.x + vWorld.z) * 0.05);
          float a = fine * 0.55 + bold * 0.95 * pulse;
          float dist = length(vWorld.xz) / ${(this.layout.size / 2).toFixed(1)};
          a *= smoothstep(1.05, 0.0, dist);
          if (a < 0.02) discard;
          gl_FragColor = vec4(col * (0.9 + pulse * 0.4), a);
        }
      `,
    });

    const mesh = new THREE.Mesh(geo, this.gridMaterial);
    mesh.position.y = 0.01;
    mesh.renderOrder = -1;
    this.group.add(mesh);

    const dark = new THREE.Mesh(
      new THREE.PlaneGeometry(size * 1.05, size * 1.05).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: 0x04060d }),
    );
    dark.position.y = 0;
    this.group.add(dark);
  }

  private buildWalls(): void {
    const half = this.layout.size / 2;
    const wallH = this.layout.wallHeight;
    const wallT = this.layout.wallThickness;

    const wallSpecs: Array<[number, number, number, number]> = [
      [0, -half, this.layout.size, wallT],
      [0,  half, this.layout.size, wallT],
      [-half, 0, wallT, this.layout.size],
      [ half, 0, wallT, this.layout.size],
    ];

    for (const [x, z, sx, sz] of wallSpecs) {
      const geo = new THREE.BoxGeometry(sx, wallH, sz);
      const mat = new THREE.MeshBasicMaterial({
        color: 0x0a1628,
        transparent: true,
        opacity: 0.85,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(x, wallH / 2, z);
      this.group.add(mesh);

      const edges = new THREE.LineSegments(
        new THREE.EdgesGeometry(geo),
        new THREE.LineBasicMaterial({ color: 0x22d3ee }),
      );
      edges.position.copy(mesh.position);
      this.group.add(edges);

      const stripGeo = new THREE.BoxGeometry(sx, 0.18, sz);
      const stripMat = new THREE.MeshBasicMaterial({ color: 0xf472b6 });
      const strip = new THREE.Mesh(stripGeo, stripMat);
      strip.position.set(x, wallH - 0.2, z);
      this.group.add(strip);
    }

    const lineGroup = new THREE.Group();
    const lineMat = new THREE.LineBasicMaterial({ color: 0xa855f7, transparent: true, opacity: 0.6 });
    for (let i = 0; i < 32; i++) {
      const t = i / 32;
      const a = t * Math.PI * 2;
      const r = half * 0.94;
      const points = [
        new THREE.Vector3(Math.cos(a) * r, 0.05, Math.sin(a) * r),
        new THREE.Vector3(Math.cos(a) * r * 0.4, 0.05, Math.sin(a) * r * 0.4),
      ];
      const geo = new THREE.BufferGeometry().setFromPoints(points);
      lineGroup.add(new THREE.Line(geo, lineMat));
    }
    this.group.add(lineGroup);
  }

  private buildObstacles(): void {
    // Shared material for all obstacle fills — same colour/opacity on every box.
    const fillMat = new THREE.MeshBasicMaterial({ color: 0x10172a, transparent: true, opacity: 0.9 });
    // Shared material for the single merged edge LineSegments.
    const edgeMat = new THREE.LineBasicMaterial({ color: 0xfb923c });

    // Accumulate all edge vertices pre-transformed to world space.
    // EdgesGeometry stores pairs of vertices — structure is preserved in the merged buffer.
    const allEdgePos: number[] = [];

    for (const ob of this.layout.obstacles) {
      const geo = new THREE.BoxGeometry(ob.hx * 2, ob.height, ob.hz * 2);

      const mesh = new THREE.Mesh(geo, fillMat);
      mesh.position.set(ob.x, ob.height / 2, ob.z);
      mesh.rotation.y = ob.rotY;
      this.group.add(mesh);

      // Extract edge vertices and bake world transform into the merged buffer.
      // Using THREE.js Y-rotation convention: x' = cos·x + sin·z, z' = -sin·x + cos·z.
      const edgesGeo = new THREE.EdgesGeometry(geo);
      const pos = edgesGeo.attributes.position as THREE.BufferAttribute;
      const sinY = Math.sin(ob.rotY);
      const cosY = Math.cos(ob.rotY);
      const wy   = ob.height / 2; // mesh world-y offset (box centred at origin)
      for (let i = 0; i < pos.count; i++) {
        const lx = pos.getX(i);
        const ly = pos.getY(i);
        const lz = pos.getZ(i);
        allEdgePos.push(
          cosY * lx + sinY * lz + ob.x,
          ly + wy,
          -sinY * lx + cosY * lz + ob.z,
        );
      }
      edgesGeo.dispose(); // temp; no longer needed after extraction
    }

    // One LineSegments for all obstacles — 1 draw call instead of 24.
    const mergedGeo = new THREE.BufferGeometry();
    mergedGeo.setAttribute("position", new THREE.Float32BufferAttribute(allEdgePos, 3));
    this.group.add(new THREE.LineSegments(mergedGeo, edgeMat));
  }

  private buildSky(): void {
    const sphere = new THREE.Mesh(
      new THREE.SphereGeometry(800, 24, 16),
      new THREE.ShaderMaterial({
        side: THREE.BackSide,
        depthWrite: false,
        uniforms: {
          uTop: { value: new THREE.Color(0x000018) },
          uMid: { value: new THREE.Color(0x0a0024) },
          uBot: { value: new THREE.Color(0x000010) },
        },
        vertexShader: /* glsl */ `
          varying vec3 vDir;
          void main() {
            vDir = normalize(position);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: /* glsl */ `
          uniform vec3 uTop;
          uniform vec3 uMid;
          uniform vec3 uBot;
          varying vec3 vDir;
          void main() {
            float h = vDir.y;
            vec3 c = mix(uBot, uMid, smoothstep(-0.4, 0.0, h));
            c = mix(c, uTop, smoothstep(0.1, 0.6, h));
            gl_FragColor = vec4(c, 1.0);
          }
        `,
      }),
    );
    this.group.add(sphere);
  }
}
